/**
 * A Vue directive for handling validation errors on form elements.
 * 
 * This directive will insert or remove error messages based on the binding value.
 * 
 * @module ValidationError
 */
export default {
  /**
   * Called when the directive is bound to the element.
   * Removes any existing error messages and inserts a new one if the binding value is truthy.
   * 
   * @param {HTMLElement} el - The element the directive is bound to.
   * @param {Object} binding - The binding object containing the value and other properties.
   */
  bind(el, binding) {
    removeError(el);
    if (binding.value) {
      insertError(el, binding.value);
    }
  },

  /**
   * Called when the element is inserted into the DOM.
   * Removes any existing error messages and inserts a new one if the binding value is truthy.
   * 
   * @param {HTMLElement} el - The element the directive is bound to.
   * @param {Object} binding - The binding object containing the value and other properties.
   */
  inserted(el, binding) {
    removeError(el);
    if (binding.value) {
      insertError(el, binding.value);
    }
  },

  /**
   * Called when the component is updated.
   * Removes any existing error messages and inserts a new one if the binding value is truthy.
   * 
   * @param {HTMLElement} el - The element the directive is bound to.
   * @param {Object} binding - The binding object containing the value and other properties.
   */
  componentUpdated(el, binding) {
    removeError(el);
    if (binding.value) {
      insertError(el, binding.value);
    }
  },
};

/**
 * Removes the error message element that follows the specified element.
 *
 * @param {HTMLElement} el - The element for which the error message should be removed.
 * @returns {void}
 */
function removeError(el) {
  const next = el.nextElementSibling;
  if (next && next.classList.contains('text-danger')) {
    next.remove();
  }
}

/**
 * Inserts an error message after the specified element.
 *
 * @param {HTMLElement} el - The element after which the error message will be inserted.
 * @param {string} message - The error message to be displayed.
 */
function insertError(el, message) {
  const div = document.createElement('div');
  div.className = 'text-danger';
  div.textContent = message;
  el.after(div);
}
